<script lang="ts">
	/**
	 * `/Transactions/Order/:uuid` — one one-time purchase, from the admin side (§13, §9.6).
	 *
	 * An order is a smaller thing than a subscription: it has already happened, and the only thing
	 * that can be done to it is give the money back. So there is one action here, and it lives
	 * where it belongs — **on the invoice**, because a refund is issued against an invoice and
	 * never against an order.
	 *
	 * The entitlements panel is not decoration. A paid order yields a **perpetual** entitlement,
	 * revocable only by refund (§4.4), so "did this purchase actually grant what it should have"
	 * is the question an admin most often arrives with — and the answer is on the same screen as
	 * the refund that would take it away.
	 */

	// The product page is the storefront's, which AbstractID serves — so this opens it there,
	// in a new tab, rather than routing to a page this console does not have.
	import { customerPathAtAbstractID } from '../../../AbstractID';
	import { onMount } from 'svelte';
	import {
		Breadcrumb,
		Button,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import {
		AcceptedTerms,
		SelectedOptions,
		EmptyState,
		PageHeader,
		Panel,
		RefundCreditNoteCell,
		RoutedBreadcrumbItem,
		RoutedLink,
		SkeletonWrapper,
		TextPlaceholder
	} from '../../../components';
	import SaleInvoices from './SaleInvoices.svelte';
	import RefundForm from '../customers/RefundForm.svelte';
	import { getAdminOrder, retryAdminRefund } from '../../../AppNetwork';
	import { availableCurrencies, formatAmount } from '../../../stores/storefront';
	import { formatCalendarDate } from '../../../Utilities';
	import { toastError, toastSuccess } from '../../../stores/toast';
	import type { AdminOrderDetail } from '../../../AppTypes';

	export let params: { uuid?: string } = {};

	let detail: AdminOrderDetail | null = null;
	let loading: boolean = true;
	let loadError: string = '';
	let busy: string = '';

	$: uuid = params.uuid ?? '';

	/** Text, never colour alone — a refunded order has to read as refunded in words. */
	const statusCopy: Record<string, string> = {
		pending: 'Payment pending',
		paid: 'Paid',
		refunded: 'Refunded',
		partially_refunded: 'Partly refunded',
		failed: 'Payment failed'
	};

	async function fnLoad(orderUUID: string): Promise<void> {
		if (orderUUID.length === 0) return;

		loading = true;
		loadError = '';
		try {
			detail = await getAdminOrder(orderUUID);
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	onMount(() => void fnLoad(uuid));

	/**
	 * Whether the purchase itself can be refunded — only when it took money and has no invoice.
	 *
	 * `paid` or `partly refunded`: a pending payment has taken nothing yet, and a refunded one has
	 * nothing left. The remaining ceiling is the server's to enforce, and it does — this decides
	 * whether the control is worth offering, not what it may do.
	 */
	$: canRefundOrder =
		detail !== null &&
		detail.invoices.length === 0 &&
		(detail.order.status === 'paid' || detail.order.status === 'partially_refunded') &&
		(detail.order.total ?? 0) > 0;

	async function fnRetryRefund(refundUUID: string): Promise<void> {
		busy = refundUUID;
		try {
			await retryAdminRefund(refundUUID);
			toastSuccess('Refund re-submitted.');
			await fnLoad(uuid);
		} catch (error) {
			toastError('The refund was not re-submitted.', (error as Error).message);
		} finally {
			busy = '';
		}
	}
</script>

<Breadcrumb class="mb-4">
	<RoutedBreadcrumbItem href="/" home>Overview</RoutedBreadcrumbItem>
	<RoutedBreadcrumbItem href="/Transactions">Transactions</RoutedBreadcrumbItem>
	<RoutedBreadcrumbItem>{detail?.order.productName ?? 'Purchase'}</RoutedBreadcrumbItem>
</Breadcrumb>

<PageHeader title={detail?.order.productName ?? 'Purchase'}>
	<svelte:fragment slot="description">One purchase, and what can be done about it.</svelte:fragment>
</PageHeader>

{#if loading}
	<SkeletonWrapper>
		<div class="widget bg-depth"><TextPlaceholder /></div>
	</SkeletonWrapper>
{:else if loadError.length > 0}
	<EmptyState variant="error" title="The purchase could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={() => void fnLoad(uuid)}>Try again</Button>
	</EmptyState>
{:else if detail !== null}
	<div class="space-y-6">
		<Panel title="Purchase" faIcon="bag-shopping">
			<svelte:fragment slot="titleRight">
				<span class="text-sm font-semibold">
					{statusCopy[detail.order.status] ?? detail.order.status}
				</span>
			</svelte:fragment>

			<dl class="grid gap-x-6 gap-y-3 sm:grid-cols-2 text-sm">
				<div>
					<dt class="card-label mb-1">Customer</dt>
					<dd>
						<RoutedLink
							to={`/Customers/${detail.customer.userUUID}`}
							class="text-primary-500 hover:underline"
						>
							{detail.customer.billingName ?? detail.customer.displayName ?? detail.customer.email}
						</RoutedLink>
						{#if detail.customer.isFlagged}
							<span class="ml-2 text-xs font-semibold text-red-600 dark:text-red-400">
								<i class="fa-solid fa-flag mr-1" />Chargeback flagged
							</span>
						{/if}
					</dd>
				</div>
				<div>
					<dt class="card-label mb-1">Product</dt>
					<dd>
						<RoutedLink
							to={customerPathAtAbstractID(`/Product/${detail.order.productSlug}`)}
							class="text-primary-500 hover:underline"
						>
							{detail.order.productName}
						</RoutedLink>
					</dd>
				</div>
				<div>
					<dt class="card-label mb-1">Total</dt>
					<!-- Formatted from the whole currency row, never ÷100 (§10). -->
					<dd class="tabular-nums">
						{$formatAmount(detail.order.total, detail.order.currency) ?? 'Amount unavailable'}
					</dd>
				</div>
				<div>
					<dt class="card-label mb-1">Bought</dt>
					<dd>
						{detail.order.paidAt === null
							? formatCalendarDate(detail.order.createdAt)
							: formatCalendarDate(detail.order.paidAt)}
					</dd>
				</div>
			</dl>
		</Panel>

		<!--
			The refund, against the invoice where there is one. §4.2 now asks the provider for an
			invoice on every one-time purchase, so this is the ordinary path.
		-->
		{#if detail.invoices.length > 0 || !canRefundOrder}
			<SaleInvoices
				invoices={detail.invoices}
				title="Invoice for this purchase"
				emptyTitle={detail.order.status === 'pending'
					? 'No invoice yet. The payment has not settled.'
					: 'No invoice for this purchase.'}
				on:refunded={() => void fnLoad(uuid)}
			/>
		{/if}

		<!--
			And against the **purchase** where there is no invoice at all.
			
			A one-time purchase sold before §4.2 asked the provider for an invoice has none, and this
			screen used to say "nothing to refund against" about money that was perfectly refundable
			at the provider — the charge is on the order itself. The invoice remains the target
			whenever one exists, so a charge is never refundable twice under two names.
		-->
		{#if detail.invoices.length === 0 && canRefundOrder}
			<Panel title="Refund this purchase" faIcon="money-bill-transfer">
				<p class="text-sm text-default mb-3">
					This purchase has no invoice, so the refund is issued against the charge itself.
				</p>
				<RefundForm
					order={detail.order}
					on:issued={() => void fnLoad(uuid)}
					on:cancel={() => undefined}
				/>
			</Panel>
		{/if}

		<!-- §4.7 — what was ticked before this was bought, in the words that were shown. -->
		<AcceptedTerms acceptances={detail.acceptedTerms} />

		<!--
			§4.8 — the options taken, and what each took off. Always rendered on a support screen,
			unlike the customer's own page: "were they given a discount for this" is a question
			support arrives with, and an absent panel cannot answer it either way.
		-->
		<SelectedOptions
			selections={detail.selectedOptions}
			currencies={$availableCurrencies}
			showKey />

		<Panel title="Refunds on this account" faIcon="money-bill-transfer">
			{#if detail.refunds.length === 0}
				<EmptyState compact title="No refunds." description="Nothing has gone back out." />
			{:else}
				<Table divClass="relative overflow-x-auto">
					<TableHead>
						<TableHeadCell>Amount</TableHeadCell>
						<TableHeadCell>Status</TableHeadCell>
						<TableHeadCell>Entitlement</TableHeadCell>
						<TableHeadCell>Note</TableHeadCell>
						<TableHeadCell>Credit note</TableHeadCell>
						<TableHeadCell />
					</TableHead>
					<TableBody>
						{#each detail.refunds as refund (refund.uuid)}
							<TableBodyRow>
								<TableBodyCell class="dataTable tabular-nums">
									{$formatAmount(refund.amount, refund.currency) ?? 'Amount unavailable'}
								</TableBodyCell>
								<TableBodyCell class="dataTable">{refund.status}</TableBodyCell>
								<TableBodyCell class="dataTable">
									{refund.revokedEntitlement ? 'Revoked' : 'Kept'}
								</TableBodyCell>
								<TableBodyCell class="dataTable">{refund.note ?? '—'}</TableBodyCell>
								<!-- §4.6. Three answers rather than two — see the component. -->
								<TableBodyCell class="dataTable">
									<RefundCreditNoteCell {refund} on:issued={() => void fnLoad(uuid)} />
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if refund.providerRefundID === null}
										<Button
											size="xs"
											color="alternative"
											disabled={busy === refund.uuid}
											on:click={() => void fnRetryRefund(refund.uuid)}
										>
											Retry
										</Button>
									{/if}
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			{/if}
		</Panel>

		<!--
			A paid order yields a **perpetual** entitlement, revocable only by refund (§4.4) — so
			"did this purchase grant what it should have" belongs on the same screen as the refund
			that would take it away.
		-->
		<Panel title="What this customer holds" faIcon="key">
			{#if detail.entitlements.length === 0}
				<EmptyState compact title="No entitlements." />
			{:else}
				<ul class="space-y-2">
					{#each detail.entitlements as entitlement (entitlement.entitlementKey)}
						<li class="widget bg-depth flex flex-wrap items-center gap-3 text-sm">
							<span class="font-mono text-xs grow">{entitlement.entitlementKey}</span>
							<span class="text-default">{entitlement.source}</span>
							<span class="font-semibold">{entitlement.status}</span>
						</li>
					{/each}
				</ul>
			{/if}
		</Panel>
	</div>
{/if}
